<template>
  <!-- 版画主题海景背景（可开关+调透明度，仅 linocut 下渲染，失焦暂停） -->
  <SeaBackground
    v-if="theme === 'linocut' && linocutBg"
    :bg-opacity="seaOpacity"
    :paused="!pageVisible"
  />
  <!-- 水纹主题 Canvas 背景（仅 ripple 下渲染） -->
  <WaterRippleBackground v-if="theme === 'ripple'" />
  <!-- 星云主题 Three.js 背景（仅 nebula 下渲染） -->
  <NebulaBackground v-if="theme === 'nebula'" />
  <!-- 粒子流主题 Canvas 背景（仅 particle 下渲染） -->
  <ParticleFlowBackground v-if="theme === 'particle'" />
  <!-- 赛博网格主题 Canvas 背景（仅 cyber 下渲染） -->
  <CyberGridBackground v-if="theme === 'cyber'" />
  <!-- 雾境主题 Canvas 背景（仅 mist 下渲染） -->
  <MistBackground v-if="theme === 'mist'" />
  <CreatureCorral />
  <!-- 吸顶导航栏 -->
  <nav class="site-nav">
    <div class="nav-brand">
      <div class="brand-logo">
        <i class="ri-book-2-line"></i>
      </div>
      <span class="brand-text char-fade-in">
        <span v-for="(ch, i) in 'AI 学习笔记'" :key="i" class="char" :style="{ '--ci': i }">{{ ch }}</span>
      </span>
    </div>
    <div class="nav-tabs" v-if="showTabs">
      <a
        class="nav-tab"
        :class="{ active: view === 'selected' }"
        @click="goSelected"
      ><i class="ri-radar-line"></i><span>每日精选</span></a>
      <a
        class="nav-tab"
        :class="{ active: view === 'home' }"
        @click="goNotes"
      ><i class="ri-book-2-line"></i><span>学习笔记</span></a>
    </div>
    <div class="nav-location" v-else>
      <span v-if="view === 'knowledge'">笔记详情</span>
      <span v-else-if="view === 'about'">关于我</span>
      <span v-else-if="view === 'stats'">学习统计</span>
      <span v-else-if="view === 'year'">年度总结</span>
    </div>
    <div class="nav-actions">
      <button
        class="cmd-trigger"
        title="搜索 / 跳转（⌘K）"
        @click="cmdPaletteOpen = true"
      ><i class="ri-search-line"></i></button>
      <div class="theme-switcher" @click.stop>
        <button
          class="theme-toggle"
          title="切换主题"
          @click="showThemePanel = !showThemePanel"
        ><i :class="themeIcon"></i></button>
        <!-- 主题选择下弹浮层 -->
        <ThemePicker
          :visible="showThemePanel"
          :current-theme="theme"
          :themes="themes"
          :sea-bg="linocutBg"
          :sea-opacity="seaOpacity"
          @select="setTheme"
          @close="showThemePanel = false"
          @toggle-sea-bg="linocutBg = !linocutBg"
          @update:sea-opacity="seaOpacity = $event"
        />
      </div>
    </div>
  </nav>
  <!-- keep-alive 缓存列表页：从详情页返回时不重新请求接口 -->
  <keep-alive include="HomePage,SelectedPage">
    <component :is="currentComponent" :push-date="currentDate" :initial-tag="currentTag" :year="currentYear" :key="compKey" />
  </keep-alive>
  <!-- 右下角：返回顶部 -->
  <div class="float-btns">
    <button
      class="float-btn float-top"
      v-show="showTop"
      title="返回顶部"
      @click="scrollTop"
    ><i class="ri-arrow-up-line"></i></button>
  </div>
  <!-- ⌘K 命令面板 -->
  <CommandPalette v-if="cmdPaletteOpen" @close="closeCmdPalette" />
</template>

<script>
import CreatureCorral from './components/CreatureCorral.vue'
import SeaBackground from './components/SeaBackground.vue'
import WaterRippleBackground from './components/WaterRippleBackground.vue'
import NebulaBackground from './components/NebulaBackground.vue'
import ParticleFlowBackground from './components/ParticleFlowBackground.vue'
import CyberGridBackground from './components/CyberGridBackground.vue'
import MistBackground from './components/MistBackground.vue'
import ThemePicker from './components/ThemePicker.vue'
import CommandPalette from './components/CommandPalette.vue'
import HomePage from './views/HomePage.vue'
import KnowledgePage from './views/KnowledgePage.vue'
import SelectedPage from './views/SelectedPage.vue'
import AboutPage from './views/AboutPage.vue'
import StatsPage from './views/StatsPage.vue'
import YearPage from './views/YearPage.vue'

/**
 * 极简路由（不引入 vue-router，保持依赖最小）
 * - 每日精选：    #/          → selected（默认首页）
 * - 学习笔记列表： #/notes     → home
 * - 学习笔记详情： #/date/YYYY-MM-DD → knowledge
 * - 关于我：      #/about     → about
 * 通过 hashchange 监听，支持浏览器前进/后退。
 *
 * tab 切换：在 selected 和 home 两个列表页之间切换，详情页/关于页隐藏 tab。
 */
export default {
  name: 'App',
  components: { CreatureCorral, SeaBackground, WaterRippleBackground, NebulaBackground, ParticleFlowBackground, CyberGridBackground, MistBackground, ThemePicker, CommandPalette, HomePage, KnowledgePage, SelectedPage, AboutPage, StatsPage, YearPage },
  data () {
    return {
      view: 'selected',
      currentDate: '',
      currentTag: '',
      currentYear: '',
      theme: 'light',
      showThemePanel: false,
      linocutBg: true,
      seaOpacity: 0.12,
      pageVisible: true,
      showTop: false,
      cmdPaletteOpen: false
    }
  },
  computed: {
    themeIcon () {
      if (this.theme === 'dark') return 'ri-moon-line'
      if (this.theme === 'linocut') return 'ri-plant-line'
      if (this.theme === 'aurora') return 'ri-sparkling-2-line'
      if (this.theme === 'ripple') return 'ri-drop-line'
      if (this.theme === 'nebula') return 'ri-planet-line'
      if (this.theme === 'particle') return 'ri-blur-off-line'
      if (this.theme === 'glass') return 'ri-contrast-drop-2-line'
      if (this.theme === 'cyber') return 'ri-grid-line'
      if (this.theme === 'mist') return 'ri-mist-line'
      return 'ri-sun-line'
    },
    themes () {
      return [
        { value: 'light', label: '亮色', icon: 'ri-sun-line', color: '#FFFFFF' },
        { value: 'dark', label: '暗色', icon: 'ri-moon-line', color: '#14171A' },
        { value: 'aurora', label: '极光', icon: 'ri-sparkling-2-line', color: 'linear-gradient(135deg, #0a0e14, #2DD4BF)' },
        { value: 'ripple', label: '水纹', icon: 'ri-drop-line', color: 'linear-gradient(135deg, #0b1218, #4FC3F7)' },
        { value: 'nebula', label: '星云', icon: 'ri-planet-line', color: 'linear-gradient(135deg, #06081a, #8B7FFF)' },
        { value: 'particle', label: '粒子', icon: 'ri-blur-off-line', color: 'linear-gradient(135deg, #08141e, #4FC3F7)' },
        { value: 'glass', label: '玻璃', icon: 'ri-contrast-drop-2-line', color: 'linear-gradient(135deg, #0a1525, #5EADAA)' },
        { value: 'cyber', label: '网格', icon: 'ri-grid-line', color: 'linear-gradient(135deg, #060d12, #50C8DC)' },
        { value: 'mist', label: '雾境', icon: 'ri-mist-line', color: 'linear-gradient(135deg, #0c0f14, #8EB0C8)' },
        { value: 'linocut', label: '版画', icon: 'ri-plant-line', color: '#f5efe0' }
      ]
    },
    currentComponent () {
      if (this.view === 'knowledge') return 'KnowledgePage'
      if (this.view === 'home') return 'HomePage'
      if (this.view === 'about') return 'AboutPage'
      if (this.view === 'stats') return 'StatsPage'
      if (this.view === 'year') return 'YearPage'
      return 'SelectedPage'
    },
    compKey () {
      // 列表页固定 key（配合 keep-alive 复用）；详情页按日期变 key
      if (this.view === 'knowledge') return 'k-' + this.currentDate
      if (this.view === 'home') return 'notes'
      if (this.view === 'about') return 'about'
      if (this.view === 'stats') return 'stats'
      if (this.view === 'year') return 'year-' + this.currentYear
      return 'selected'
    },
    showTabs () {
      // 详情页/关于页/统计页/年度总结页隐藏 tab
      return this.view !== 'knowledge' && this.view !== 'about' && this.view !== 'stats' && this.view !== 'year'
    }
  },
  mounted () {
    this.initTheme()
    this.syncFromHash()
    window.addEventListener('hashchange', this.syncFromHash)
    window.addEventListener('scroll', this.onScroll)
    document.addEventListener('click', this.closeThemePanel)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    document.addEventListener('keydown', this.onGlobalKeydown)
    document.addEventListener('mousemove', this.onSpotlightMove, { passive: true })
  },
  beforeUnmount () {
    window.removeEventListener('hashchange', this.syncFromHash)
    window.removeEventListener('scroll', this.onScroll)
    document.removeEventListener('click', this.closeThemePanel)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    document.removeEventListener('keydown', this.onGlobalKeydown)
    document.removeEventListener('mousemove', this.onSpotlightMove)
  },
  watch: {
    linocutBg (val) {
      localStorage.setItem('aid-linocut-bg', val ? '1' : '0')
    },
    seaOpacity (val) {
      localStorage.setItem('aid-sea-opacity', String(val))
    }
  },
  methods: {
    syncFromHash () {
      const hash = window.location.hash || ''
      const m = hash.match(/^#\/date\/(\d{4}-\d{2}-\d{2})/)
      const mt = hash.match(/^#\/notes\/tag\/(.+)$/)
      const my = hash.match(/^#\/year(?:\/(\d{4}))?/)
      if (m) {
        this.view = 'knowledge'
        this.currentDate = m[1]
        this.currentTag = ''
      } else if (mt) {
        // 标签筛选：#/notes/tag/:tag → 列表页并激活标签
        this.view = 'home'
        this.currentDate = ''
        this.currentTag = decodeURIComponent(mt[1])
      } else if (my && hash.startsWith('#/year')) {
        // 年度总结：#/year/2026（缺省当前年）
        this.view = 'year'
        this.currentDate = ''
        this.currentTag = ''
        this.currentYear = my[1] || String(new Date().getFullYear())
      } else if (hash.startsWith('#/about')) {
        this.view = 'about'
        this.currentDate = ''
        this.currentTag = ''
      } else if (hash.startsWith('#/stats')) {
        this.view = 'stats'
        this.currentDate = ''
        this.currentTag = ''
      } else if (hash.startsWith('#/notes')) {
        this.view = 'home'
        this.currentDate = ''
        this.currentTag = ''
      } else {
        // #/ 或空 → 精选页
        this.view = 'selected'
        this.currentDate = ''
        this.currentTag = ''
      }
    },
    goSelected () {
      window.location.hash = '#/'
    },
    goNotes () {
      window.location.hash = '#/notes'
    },
    goToDate (d) {
      window.location.hash = `#/date/${d}`
    },
    // ── 主题切换（亮色 / 暗色 / 版画） ──
    initTheme () {
      const saved = localStorage.getItem('aid-theme')
      if (saved && ['light', 'dark', 'aurora', 'ripple', 'nebula', 'particle', 'glass', 'cyber', 'mist', 'linocut'].indexOf(saved) >= 0) {
        this.theme = saved
      } else {
        this.theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      const savedBg = localStorage.getItem('aid-linocut-bg')
      this.linocutBg = savedBg !== '0'
      // 海景背景透明度：0~0.3，默认 0.12
      const savedOp = parseFloat(localStorage.getItem('aid-sea-opacity'))
      if (!isNaN(savedOp) && savedOp >= 0 && savedOp <= 0.3) {
        this.seaOpacity = savedOp
      }
      this.applyTheme()
    },
    onOpacityInput (e) {
      this.seaOpacity = parseFloat(e.target.value)
    },
    onVisibilityChange () {
      this.pageVisible = !document.hidden
    },
    setTheme (val) {
      this.theme = val
      this.showThemePanel = false
      this.applyTheme()
    },
    closeThemePanel () {
      this.showThemePanel = false
    },
    // ── ⌘K / Ctrl+K 打开命令面板 ──
    onGlobalKeydown (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.cmdPaletteOpen = true
      }
    },
    closeCmdPalette () {
      this.cmdPaletteOpen = false
    },
    // ── spotlight 追光：监听卡片上的鼠标位置 ──
    onSpotlightMove (e) {
      const target = e.target.closest('.spotlight')
      if (!target) return
      const rect = target.getBoundingClientRect()
      target.style.setProperty('--mx', (e.clientX - rect.left) + 'px')
      target.style.setProperty('--my', (e.clientY - rect.top) + 'px')
    },
    applyTheme () {
      document.documentElement.setAttribute('data-theme', this.theme)
      localStorage.setItem('aid-theme', this.theme)
    },
    // ── 返回顶部 ──
    onScroll () {
      this.showTop = window.scrollY > 400
    },
    scrollTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style>
/* 全局样式 */
@import './assets/style.css';

#app {
  position: relative;
  z-index: 1;
}

/* 吸顶导航栏 */
.site-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 56px;
  background: color-mix(in srgb, var(--c-surface) 82%, transparent);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  max-width: 760px;
  margin: 0 auto;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-logo {
  width: 20px;
  height: 20px;
  background: var(--c-accent);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
}

.brand-text {
  font-size: 15px;
  font-weight: 700;
  color: var(--c-text);
  letter-spacing: -0.02em;
}

.brand-text .char {
  display: inline-block;
}

.nav-tabs {
  display: flex;
  gap: 4px;
  padding: 3px;
  background: var(--c-surface-2);
  border-radius: 999px;
}

.nav-tab {
  padding: 6px 16px;
  font-size: 14px;
  color: var(--c-text-2);
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-tab:hover {
  color: var(--c-text);
}

.nav-tab.active {
  background: var(--c-surface);
  color: var(--c-text);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.nav-location {
  font-size: 13px;
  color: var(--c-text-3);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cmd-trigger {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--c-text-2);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.cmd-trigger:hover {
  background: var(--c-surface-2);
  color: var(--c-accent);
}

.theme-toggle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--c-text-2);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.theme-toggle:hover {
  background: var(--c-surface-2);
  color: var(--c-accent);
}

/* 主题选择面板 */
.theme-switcher {
  position: relative;
}

.theme-panel {
  position: absolute;
  top: 44px;
  right: 0;
  width: 168px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 12px;
  box-shadow: var(--shadow-pop);
  padding: 10px;
  z-index: 200;
}

.theme-panel-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--c-text-3);
  padding: 4px 8px 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.theme-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--c-text);
  transition: background 0.15s;
}

.theme-option:hover {
  background: var(--c-surface-2);
}

.theme-option.active {
  background: var(--c-accent-soft);
  color: var(--c-accent-text);
  font-weight: 600;
}

.theme-dot {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid var(--c-border);
  flex-shrink: 0;
}

.theme-option-label {
  flex: 1;
}

.theme-check {
  color: var(--c-accent);
  font-size: 16px;
}

.theme-divider {
  height: 1px;
  background: var(--c-border);
  margin: 8px 4px;
}

.theme-bg-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--c-text-2);
  transition: background 0.15s;
}

.theme-bg-toggle:hover {
  background: var(--c-surface-2);
}

.theme-bg-toggle i {
  color: var(--c-accent);
  font-size: 16px;
}

/* 海景背景透明度调节 */
.opacity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
}

.opacity-label {
  font-size: 13px;
  color: var(--c-text-2);
  flex-shrink: 0;
}

.opacity-slider {
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--c-surface-2);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--c-accent);
  border: 2px solid var(--c-surface);
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--c-accent);
  border: 2px solid var(--c-surface);
  cursor: pointer;
}

.opacity-val {
  font-size: 12px;
  color: var(--c-text-3);
  min-width: 32px;
  text-align: right;
  flex-shrink: 0;
}

.theme-panel-fade-enter-active,
.theme-panel-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.theme-panel-fade-enter-from,
.theme-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* 右下角悬浮按钮（返回顶部） */
.float-btns {
  position: fixed;
  right: 16px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 300;
}

.float-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  box-shadow: var(--shadow-pop);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  user-select: none;
}

.float-btn:hover {
  border-color: var(--c-accent);
  color: var(--c-accent);
  transform: translateY(-2px);
}

/* 响应式：每日精选 / 学习笔记 tab 移动端适配（三级渐进收敛） */
@media (max-width: 768px) {
  /* 平板/大屏手机：导航收窄内边距，tab 紧凑化，文字保留 */
  .site-nav {
    padding: 0 12px;
  }

  .brand-text {
    font-size: 14px;
  }

  .nav-tabs {
    gap: 2px;
    padding: 2px;
    flex-shrink: 1;
    overflow: hidden;
  }

  .nav-tab {
    padding: 5px 10px;
    font-size: 13px;
    gap: 4px;
    white-space: nowrap;
  }

  .nav-tab i {
    font-size: 15px;
  }
}

@media (max-width: 640px) {
  /* 手机：品牌只留 logo 图标，腾出空间让 tab 保持图标+文字 */
  .brand-text {
    display: none;
  }

  .nav-brand {
    gap: 0;
  }

  .nav-tab {
    padding: 6px 12px;
  }
}

@media (max-width: 420px) {
  /* 窄屏手机：tab 只显示图标，文字省略 */
  .nav-tab span {
    display: none;
  }

  .nav-tab {
    padding: 6px 14px;
    gap: 0;
  }

  .nav-tab i {
    font-size: 17px;
  }
}
</style>
